import * as React from 'react';
import { authenticationService, User } from '../../services/authentication';
import { PageRoutes } from '../root/Routes';
import VerificationCodeComponent, { NullableCodeDigit } from './VerificationCodeComponent';
import { extractQueryParameters, QueryParameters } from './authenticationUtility';
import { errorHandler } from '../../services/errorHandler';
import { LogManager } from '../../utils/LogManager';
import { Event } from 'fs-api-client';
import './authentication.css';
import { PasswordValidator } from '../../services/validators/PasswordValidator';
import { DEFAULT_DIGIT_INPUT_COUNT, DONT_HAVE_AN_ACCOUNT_YET_MESSAGE, SIGN_UP_MESSAGE } from '../../utils/constants';
import { CognitoException } from './errors/CognitoErrors';
import { PasswordComplexityIndicator } from '../common/requirementManagers/PasswordComplexityIndicator';
import { LockIcon, renderWaitingForSuccessElement } from '../common/Icons';
import { AuthenticationFooter } from '../common/authenticationCardComponents/AuthenticationFooter';
import { CardHeader } from '../common/authenticationCardComponents/CardHeader';
import { LucidLinkLogo } from '../common/Icons';
import { LUCIDLINK_WEBSITE } from '../../utils/constants';
import { VERIFICATION_MESSAGE } from '../../../backoffice/utils/constants';

const logManager = new LogManager('ResetPassword');

interface ResetPasswordState {
    userToReset: string;
    code: number[];
    showPassword: boolean;
    newPassword: string;
    wrongCode: boolean;
    loading: boolean;
    formErrorMessage: string | null;
    passwordErrorMessage: JSX.Element | null;
    verificationCodeEntered: boolean;
    isPasswordInputPristine: boolean;
    verifyingCode: boolean;
}

class ResetPassword extends React.Component<any, ResetPasswordState> {
    private verificationCodeComponent: any;
    constructor(props: any) {
        super(props);

        const queryParameters: QueryParameters = extractQueryParameters(this.props.location);
        const userToReset = queryParameters.user || this.props.history?.location?.props?.userToReset;
        const code = queryParameters.code ? queryParameters.code : new Array(DEFAULT_DIGIT_INPUT_COUNT);

        this.verificationCodeComponent = React.createRef();

        this.handleSendAgainClick = this.handleSendAgainClick.bind(this);

        this.state = {
            userToReset,
            code,
            newPassword: '',
            showPassword: false,
            loading: false,
            formErrorMessage: null,
            passwordErrorMessage: null,
            wrongCode: false,
            verificationCodeEntered: !!queryParameters.code,
            isPasswordInputPristine: true,
            verifyingCode: false
        };
    }

    componentDidMount() {
        if (!this.state.userToReset) {
            this.props.history.push(PageRoutes.Login);
            return;
        }
    }

    render() {
        const errorMessage =
        (
            <span className="red center">{this.state.formErrorMessage}</span>
        );
        const successMessage =
        (
            <p className="mb-0">
                A verification code has been sent to
                <br />
                <strong>{this.state.userToReset}</strong>.
            </p>
        );
        return (
            <div className="auth">
                <div className="auth_wrapper">
                    <header>
                        <a href={LUCIDLINK_WEBSITE}>
                            <LucidLinkLogo width={'130px'} height={'50px'}/>
                        </a>
                    </header>
                    <div className="auth_block">
                        <div>
                            <CardHeader
                                message={'Reset your password'}
                                verifyEmail={successMessage}
                                email={this.state.userToReset}
                                history={this.props.history}
                                step={PageRoutes.Login}
                            />
                            <div className="auth_form center">
                                {this.state.verifyingCode && renderWaitingForSuccessElement(VERIFICATION_MESSAGE)}
                                {!this.state.verifyingCode && <div className="float-label-control no__animation six-digits ml-0 mr-0">
                                    <VerificationCodeComponent
                                        onCodeChanged={(code: NullableCodeDigit[]) => {this.handleCodeChange(code); }}
                                        ref={this.verificationCodeComponent}
                                        onCodeEntered={this.onCodeEntered}
                                        onCodeDigitChanged={this.onCodeDigitChanged}
                                        initialCode={this.state.code}
                                    />
                                    <label style={{marginTop: '20px'}}>Enter your verification code</label>
                                    <div className="row">
                                        <span className="minitext pt-2">
                                            {this.state.formErrorMessage && errorMessage}
                                        </span>
                                    </div>
                                </div>}
                                {!this.state.verifyingCode && <div className="float-label-control" style={{width: '100%'}}>
                                    <input
                                        type={this.state.showPassword ? 'input' : 'password'}
                                        className="form-control"
                                        placeholder="New password"
                                        name="password"
                                        id="password"
                                        autoComplete="new-password"
                                        autoFocus={true}
                                        onChange={event => this.handlePasswordChange(event)}
                                        onKeyDown={async (e) => {await this.handleEnter(e); }}
                                    />
                                    <label>New password</label>
                                    <span className="icon">
                                        <LockIcon
                                            width={'17px'}
                                            height={'19px'}
                                        />
                                    </span>
                                    {this.getPasswordValidationErrorMessage()}
                                </div>}
                            </div>
                            <div>
                                <button
                                    id="sign_up"
                                    type="submit"
                                    className={this.state.loading ? 'loading' : 'btn'}
                                    disabled={this.state.loading ||
                                        !this.validatePassword() ||
                                        !this.state.verificationCodeEntered}
                                    onClick={this.onSubmit}
                                >
                                    Reset password
                                </button>
                                <p className="minitext center pt-2 mb-0">Haven't received an email? <br/> Check your spam folder or <a href="#" onClick={this.handleSendAgainClick}>Send again</a></p>
                            </div>
                        </div>
                    </div>
                    <AuthenticationFooter message={{text: DONT_HAVE_AN_ACCOUNT_YET_MESSAGE, label: SIGN_UP_MESSAGE}}/>
                </div>
            </div>
        );
    }

    private handleCodeChange(code: NullableCodeDigit[]): void {
        this.setState({verificationCodeEntered: !code.includes(null)});
    }

    private async handleSendAgainClick(
        event: React.MouseEvent<HTMLAnchorElement>
    ): Promise<void> {
        event.preventDefault();
        logManager.log(
            `"Send again" link clicked for email: ${this.state.userToReset}`,
            true,
            Event.EventTypeEnum.Signups
        );
        this.setState({ verifyingCode: true });
        try {
            await authenticationService.resetPassword(this.state.userToReset);
            this.setState({ formErrorMessage: null, verifyingCode: false, newPassword: '' });
        } catch (error) {
            logManager.error(error);
            this.setState({ formErrorMessage: error.message, verifyingCode: false });
        }
    }

    private onCodeEntered = (code: number[]) => {

        this.setState({
            code
        });
    }

    private validatePassword(): boolean {
        return PasswordValidator.validatePassword(this.state.newPassword);
    }

    private onCodeDigitChanged = () => {
        this.clearValidation();
    }

    private handlePasswordChange = (event: any) => {
        this.clearValidation();
        let value = event.target.value;
        if (value) { value = value.trim(); }

        this.setState({ newPassword: value });
    }

    private getPasswordValidationErrorMessage(): JSX.Element | null {
       return PasswordComplexityIndicator({password: this.state.newPassword});
    }

    private onSubmit = () => {
        const passwordValidationErrorMessage = this.getPasswordValidationErrorMessage();
        if (passwordValidationErrorMessage) {
            this.setState({
                passwordErrorMessage: passwordValidationErrorMessage
            });
        }

        const codeAsString = this.state.code.reduce(
            (codeString, codeFragment) => {
                return codeString + codeFragment;
            },
            ''
        );

        const user = new User(this.state.userToReset, this.state.newPassword);

        this.setState(
            { loading: true },
            () => {
                authenticationService
                    .confirmNewPassword(user, codeAsString)
                    .then(this.onConfirmationSuccess)
                    .catch(this.onConfirmationError);
            }
        );
    }

    private onConfirmationSuccess = (result: any) => {
        this.props.history.push({
            pathname: PageRoutes.Login,
            props: {
                email: this.state.userToReset,
                step: PageRoutes.ResetPassword
            },
            search: `?confirmedEmail=${encodeURIComponent(this.state.userToReset)}`
        });
    }

    private onConfirmationError = (error: any) => {
        this.setState(
            { loading: false },
            () => {
                const stateChange = { formErrorMessage: error.message };
                switch (error.code) {
                    case CognitoException.CodeMismatchException:
                    case CognitoException.InvalidParameterException:
                        this.setState({ ...stateChange, wrongCode: true });
                        break;
                    case CognitoException.UserNotFoundException:
                    case CognitoException.ExpiredCodeException:
                        this.props.history.push(PageRoutes.ResetCodeExpired);
                        break;
                    default:
                        errorHandler(error);
                        break;
                }
            }
        );
    }

    private async handleEnter(event: any) {
        const ENTER_KEYCODE = 13;
        if (this.state.newPassword !== '') {
            if (event.keyCode === ENTER_KEYCODE) {
                await this.onSubmit();
            }
        }
    }

    private clearValidation = () => {
        this.setState({
            wrongCode: false,
            formErrorMessage: null,
            passwordErrorMessage: null
        });
    }
}

export default ResetPassword;



// WEBPACK FOOTER //
// ./src/webportal/components/authentication/ResetPassword.tsx